import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { SubmissionsService } from 'src/app/services/submissions.service';
import { ApexNonAxisChartSeries, ApexChart, ApexTitleSubtitle, ApexLegend, ApexDataLabels, ApexResponsive, ApexFill, ApexPlotOptions } from 'ng-apexcharts';

export type ChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    labels: string[];
    colors: string[];
    title: ApexTitleSubtitle;
    legend: ApexLegend;
    dataLabels: ApexDataLabels;
    fill: ApexFill;
    plotOptions: ApexPlotOptions;
    responsive: ApexResponsive[];
};

@Component({
    selector: 'app-language-distribution-donut',
    templateUrl: './language-distribution-donut.component.html',
    styleUrls: ['./language-distribution-donut.component.scss']
})
export class LanguageDistributionDonutComponent implements OnInit, OnChanges {
    @Input() title: string;
    @Input() isTranslated: boolean = false;
    @Input() isProfessor: boolean = false;
    @Input() filterType: string;

    years: number[] = [];
    selectedYear: number;
    chartOptions: any;
    languageData: { label: string, value: number, color: string }[] = [];
    totalSubmissions: number = 0;
    isLoading: boolean = false;
    submissionsData: any[] = [];
    apiTotalSubmissions: number = 0;

    mainLanguages = ['English', 'Norwegian', 'Swedish'];

    languageMap = {
        'en': 'English',
        'sq': 'Albanian',
        'de': 'German',
        'it': 'Italian',
        'fr': 'French',
        'es': 'Spanish',
        'el': 'Greek',
        'cs': 'Czech',
        'tr': 'Turkish',
        'sk': 'Slovak',
        'lt': 'Lithuanian',
        'lv': 'Latvian',
        'pl': 'Polish',
        'sr': 'Serbian',
        'mk': 'Macedonian',
        'pt': 'Portuguese',
        'nl': 'Dutch',
        'ru': 'Russian',
        'bg': 'Bulgarian',
        'hu': 'Hungarian',
        'ro': 'Romanian',
        'sl': 'Slovenian',
        'sv': 'Swedish',
        'fi': 'Finnish',
        'hr': 'Croatian',
        'bs': 'Bosnian',
        'no': 'Norwegian',
        'da': 'Danish',
        'et': 'Estonian'

    };

    colorPalette: string[] = [
        '#00407F', '#0059B2', '#2994FF', '#3399CC', '#2A6FA1',
        '#3BAED4', '#0084FF', '#66B2FF', '#3399FF', '#0066CC'
    ];

    constructor(private submissionsService: SubmissionsService) { }

    ngOnInit(): void {
        this.initEmptyChart();

        const currentYear = new Date().getFullYear();
        this.years = Array.from({ length: 5 }, (_, i) => currentYear - i).reverse();
        this.selectedYear = currentYear;

        this.loadSubmissionsData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filterType'] && !changes['filterType'].firstChange) {
            this.loadSubmissionsData();
        }
    }

    initEmptyChart() {
        this.chartOptions = {
            series: [100],
            chart: {
                type: 'donut',
                height: 500,
                width: 500
            },
            colors: [this.colorPalette[0]],
            legend: {
                show: false
            },
            dataLabels: {
                enabled: false
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%'
                    }
                }
            },
            responsive: [{
                breakpoint: 480,
                options: {
                    chart: {
                        width: '100%'
                    }
                }
            }]
        };
    }

    loadSubmissionsData() {
        this.isLoading = true;
        this.totalSubmissions = 0;
        this.apiTotalSubmissions = 0;
        this.initEmptyChart();
        const type = this.isTranslated ? 'translated' : 'original';
        this.submissionsService.getLanguageDistribution(this.selectedYear.toString(), type, this.isProfessor, this.filterType).subscribe(
            response => {
                this.isLoading = false;
                if (response && response.languages) {
                    const maxMain = this.isTranslated ? 4 : 3;
                    const sorted = response.languages.slice();
                    const mainLanguages = sorted.slice(0, maxMain);
                    const others = sorted.slice(maxMain);

                    let languageData = mainLanguages.map((lang, i) => ({
                        label: this.getLanguageName(lang.language),
                        value: lang.count,
                        color: this.colorPalette[i % this.colorPalette.length]
                    }));

                    if (others.length > 0) {
                        const othersCount = others.reduce((sum, lang) => sum + lang.count, 0);
                        languageData.push({
                            label: 'Others',
                            value: othersCount,
                            color: this.colorPalette[languageData.length % this.colorPalette.length]
                        });
                    }

                    this.languageData = languageData;

                    if (response.type === 'translated') {
                        this.totalSubmissions = response.totalLanguageOccurrences;
                        this.apiTotalSubmissions = response.totalSubmissions;
                    } else {
                        this.totalSubmissions = response.totalLanguageOccurrences || response.languages.reduce((sum, lang) => sum + lang.count, 0);
                        this.apiTotalSubmissions = response.totalSubmissions;
                    }
                    this.updateChart();
                } else {
                    this.languageData = [];
                    this.totalSubmissions = 0;
                    this.apiTotalSubmissions = 0;
                    this.initEmptyChart();
                }
            },
            error => {
                this.isLoading = false;
                this.languageData = [];
                this.totalSubmissions = 0;
                this.apiTotalSubmissions = 0;
                this.initEmptyChart();
            }
        );
    }

    getLanguageName(code: string): string {
        if (this.isTranslated && (code === '' || code === undefined || code === null || code === '[]')) {
            return 'None';
        }
        if (!code) return 'Unknown';

        if (typeof code === 'string' && code.startsWith('[') && code.endsWith(']')) {
            try {
                const parsedArray = JSON.parse(code);
                if (Array.isArray(parsedArray)) {
                    if (parsedArray.length === 0) return 'None';
                    return parsedArray.map(lang => this.languageMap[lang.toLowerCase()] || lang).join(', ');
                }
            } catch (e) {
            }
        }

        const normalizedCode = code.toLowerCase();

        return this.languageMap[normalizedCode] || code;
    }

    processLanguageDistribution() {
        if (!this.submissionsData || this.submissionsData.length === 0) {
            this.languageData = [];
            this.totalSubmissions = 0;
            this.initEmptyChart();
            return;
        }

        const languageCounts = {};
        let totalLanguageEntries = 0;

        this.submissionsData.forEach(submission => {
            let languageField = this.isTranslated ? submission.translatedLanguage : submission.originalLanguage;
            let languageCodes: string[] = [];
            if (this.isTranslated && typeof languageField === 'string' && languageField.trim().startsWith('[')) {
                try {
                    const parsed = JSON.parse(languageField);
                    if (Array.isArray(parsed)) {
                        if (parsed.length === 0) {
                            languageCodes = ['None'];
                        } else {
                            languageCodes = parsed.map(code => (code === '' || code === null || code === undefined) ? 'None' : code);
                        }
                    } else {
                        languageCodes = [languageField];
                    }
                } catch {
                    languageCodes = [languageField];
                }
            } else if (this.isTranslated && (languageField === '' || languageField === undefined || languageField === null || languageField === '[]')) {
                languageCodes = ['None'];
            } else if (languageField !== undefined && languageField !== null) {
                languageCodes = [languageField];
            }

            languageCodes.forEach(code => {
                const language = this.getLanguageName(code);
                if (language === 'Unknown') return;
                if (!languageCounts[language]) {
                    languageCounts[language] = 0;
                }
                languageCounts[language]++;
                totalLanguageEntries++;
            });
        });

        const mainLanguageData = [];
        let othersCount = 0;

        this.mainLanguages.forEach(lang => {
            if (languageCounts[lang]) {
                mainLanguageData.push({
                    label: lang,
                    value: languageCounts[lang],
                    color: this.colorPalette[mainLanguageData.length % this.colorPalette.length]
                });
                delete languageCounts[lang];
            }
        });
        if (languageCounts['None']) {
            mainLanguageData.push({
                label: 'None',
                value: languageCounts['None'],
                color: this.colorPalette[mainLanguageData.length % this.colorPalette.length]
            });
            delete languageCounts['None'];
        }

        Object.keys(languageCounts).forEach(lang => {
            othersCount += languageCounts[lang];
        });

        if (othersCount > 0) {
            mainLanguageData.push({
                label: 'Others',
                value: othersCount,
                color: this.colorPalette[mainLanguageData.length % this.colorPalette.length]
            });
        }

        mainLanguageData.sort((a, b) => b.value - a.value);

        this.languageData = mainLanguageData;

        if (this.isTranslated) {
            this.totalSubmissions = totalLanguageEntries;
            this.apiTotalSubmissions = this.submissionsData.length;
        } else {
            this.totalSubmissions = totalLanguageEntries;
            this.apiTotalSubmissions = this.submissionsData.length;
        }
        this.updateChart();
    }

    updateChart() {
        this.chartOptions = {
            series: this.languageData.map(d => d.value),
            labels: this.languageData.map(d => d.label),
            chart: {
                type: 'donut',
                height: 500,
                width: 500
            },
            colors: this.languageData.map(d => d.color),
            legend: {
                show: true,
                position: 'bottom',
                fontSize: '18px',
                markers: { radius: 12 }
            },
            dataLabels: {
                enabled: true,
                formatter: (val, opts) => {
                    const percentage = (this.languageData[opts.seriesIndex].value / this.totalSubmissions) * 100;
                    return `${Math.round(percentage)}%`;
                },
                style: {
                    fontSize: '20px',
                    fontWeight: 600,
                    colors: ['#fff']
                }
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%'
                    }
                }
            },
            responsive: [{
                breakpoint: 1200,
                options: {
                    chart: {
                        width: '100%',
                        height: 350
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            }, {
                breakpoint: 480,
                options: {
                    chart: {
                        width: '100%',
                        height: 300
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            }]
        };
    }

    onYearChange() {
        this.loadSubmissionsData();
    }
} 